// Mockup secim modali — TEK yerde.
//
// Satan mockup atamasi ve "mockup ekle" akislari ayni ihtiyaci paylasiyordu:
// tum havuzda arama, klasore gore suzme, baski alani olmayanin uyarisi ve
// alanin yerinde duzenlenmesi. Iki ayri dar acilir kutu yerine ortak bir
// modal: kurallar ayrisamaz.

import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, FolderMinus, FolderPlus, Loader2, Pencil, Search, Star, X } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { DesignAreaEditor, type AreaRect } from "./DesignAreaEditor";
import { useSupplierVariations } from "../../lib/useSupplierVariations";
import type { CustomMockup } from "@erc/shared";

const abs = (u?: string | null) =>
  !u ? "" : u.startsWith("http") || u.startsWith("data:") ? u : `${getBackendUrl()}${u}`;

export interface ChooserMockup {
  id: string;
  label: string;
  imageUrl: string;
  thumbnailUrl?: string | null;
  brand?: string | null;
  model?: string | null;
  color?: string | null;
  theme?: string;
  folderId?: string | null;
  isSelling?: boolean;
  bigPrint?: boolean;
  productTypeKey?: string | null;
  designAreaW?: number;
  designAreaH?: number;
  designAreas?: unknown[];
  createdAt?: string;
}

/** Baski alani tanimli mi — alan yoksa tasarim yerlestirilemez */
export function mockupHasArea(m: ChooserMockup): boolean {
  if (Array.isArray(m.designAreas) && m.designAreas.length > 0) return true;
  return (m.designAreaW ?? 0) > 20 && (m.designAreaH ?? 0) > 20;
}

export function MockupChooserModal({
  title,
  mode,
  selectedIds,
  excludeIds,
  productTypeKey,
  compare,
  inspiration,
  newestFirst,
  current,
  collection,
  testDesignLight,
  testDesignDark,
  apparel,
  onPick,
  onClose,
}: {
  /** Giyim listesi: alan duzenleyici analiz formuyla acilir (AI ile kur analizi de kaydeder) */
  apparel?: boolean;
  /** Alan duzenleyicide gosterilecek ornek tasarimlar (mockup hatalarini gormek icin) */
  testDesignLight?: string | null;
  testDesignDark?: string | null;
  title: string;
  /** single: sec ve kapat · multi: tikladikca ekle */
  mode: "single" | "multi";
  selectedIds?: string[];
  excludeIds?: string[];
  /** Verilirse "yalnizca bu urun tipi" suzgeci sunulur */
  productTypeKey?: string | null;
  /** Karsilastirma icin ust barda gosterilecek mevcut secim */
  compare?: { label: string; imageUrl: string } | null;
  /** Ilham listesinin fotografi — sagda BUYUK gosterilir (satan mockup'i
   *  secerken rakibin kullandigi gorselle yan yana karsilastirmak icin) */
  inspiration?: { imageUrl: string; title?: string | null } | null;
  /** true: en son eklenen mockuplar en ustte */
  newestFirst?: boolean;
  /** Verilirse ustte "bu listedeki mockuplar" duzenlenir (sira, ana gorsel, cikar);
   *  havuzdan secilen BASA eklenir (ana gorsel olur), pencere acik kalir */
  current?: { ids: string[]; onChange: (ids: string[]) => void };
  /** Listenin mockup grubu: ustteki kartlardan gruba ekle / gruptan cikar */
  collection?: { name: string; mockupIds: string[]; onChange: (ids: string[]) => Promise<void> };
  onPick: (m: ChooserMockup) => void;
  onClose: () => void;
}) {
  const supplierVars = useSupplierVariations();
  const [all, setAll] = useState<ChooserMockup[]>([]);
  const [folders, setFolders] = useState<Array<{ id: string; name: string; parentId: string | null }>>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [folderId, setFolderId] = useState("");
  const [onlyStarred, setOnlyStarred] = useState(false);
  const [onlyType, setOnlyType] = useState(Boolean(productTypeKey));
  const [onlyWithArea, setOnlyWithArea] = useState(false);
  const [areaMockup, setAreaMockup] = useState<CustomMockup | null>(null);
  // Yerel kopya: sunucu denetimi tazelenene kadar ust uste tiklamalar kaybolmasin
  const [cur, setCur] = useState<string[]>(current?.ids ?? []);
  const [coll, setColl] = useState<string[]>(collection?.mockupIds ?? []);
  const [collBusy, setCollBusy] = useState<string | null>(null);
  const setCurrent = (ids: string[]) => { setCur(ids); current?.onChange(ids); };
  async function toggleCollection(id: string) {
    if (!collection) return;
    const next = coll.includes(id) ? coll.filter((x) => x !== id) : [...coll, id];
    setCollBusy(id);
    try { await collection.onChange(next); setColl(next); } catch { /* kaydedilemedi */ }
    setCollBusy(null);
  }

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ kind: "mockup" });
    if (productTypeKey && onlyType) qs.set("productTypeKey", productTypeKey);
    fetch(`${getBackendUrl()}/api/mockups?${qs}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((j) => setAll(j.data ?? []))
      .catch(() => { /* liste alinamadi */ })
      .finally(() => setLoading(false));
  }, [productTypeKey, onlyType]);

  useEffect(() => {
    fetch(`${getBackendUrl()}/api/mockups/folders?kind=mockup`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((j) => setFolders(j.data ?? []))
      .catch(() => { /* klasorler alinamadi */ });
  }, []);

  /** Klasor agaci girintili liste + alt klasorler dahil suzme */
  const folderOptions = useMemo(() => {
    const byParent: Record<string, typeof folders> = {};
    folders.forEach((f) => { const k = f.parentId ?? "root"; (byParent[k] ||= []).push(f); });
    const out: { id: string; label: string }[] = [];
    const walk = (parent: string, depth: number) => {
      (byParent[parent] || []).forEach((f) => {
        out.push({ id: f.id, label: `${"— ".repeat(depth)}${f.name}` });
        walk(f.id, depth + 1);
      });
    };
    walk("root", 0);
    return out;
  }, [folders]);

  const folderMatch = useMemo(() => {
    if (!folderId) return null;
    const ids = new Set<string>([folderId]);
    let grew = true;
    while (grew) {
      grew = false;
      for (const f of folders) {
        if (f.parentId && ids.has(f.parentId) && !ids.has(f.id)) { ids.add(f.id); grew = true; }
      }
    }
    return ids;
  }, [folderId, folders]);

  const excluded = new Set([...(excludeIds ?? []), ...(current ? cur : [])]);
  const selected = new Set(selectedIds ?? []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const parts = needle ? needle.split(/\s+/) : [];
    const list = all.filter((m) => {
      if (excluded.has(m.id)) return false;
      if (onlyStarred && !m.isSelling) return false;
      if (onlyWithArea && !mockupHasArea(m)) return false;
      if (folderMatch && !(m.folderId && folderMatch.has(m.folderId))) return false;
      if (parts.length === 0) return true;
      const hay = [m.label, m.brand, m.model, m.color, m.productTypeKey].filter(Boolean).join(" ").toLowerCase();
      return parts.every((w) => hay.includes(w));
    });
    // Yildizlilar basa
    if (newestFirst) {
      return [...list].sort((a, b) => (Date.parse(b.createdAt ?? "") || 0) - (Date.parse(a.createdAt ?? "") || 0));
    }
    return [...list].sort((a, b) => Number(Boolean(b.isSelling)) - Number(Boolean(a.isSelling)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, q, folderMatch, onlyStarred, onlyWithArea, excludeIds, cur]);

  /** Alan duzenleyiciden gelen alanlari kaydet (Mockup Yonetimi ile ayni sema) */
  async function saveAreas(mockupId: string, areas: AreaRect[]) {
    const first = areas[0] as (AreaRect & { theme?: "light" | "dark" }) | undefined;
    try {
      await fetch(`${getBackendUrl()}/api/mockups/${mockupId}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({
          designAreas: areas,
          theme: first?.theme ?? "light",
          designAreaX: first ? Math.round(first.x) : 0,
          designAreaY: first ? Math.round(first.y) : 0,
          designAreaW: first ? Math.round(first.w) : 0,
          designAreaH: first ? Math.round(first.h) : 0,
          designAreaRotation: first ? Math.round(first.rotation || 0) : 0,
        }),
      });
      // Listeyi guncelle: alan uyarisi hemen kalksin
      setAll((prev) => prev.map((x) => (x.id === mockupId ? { ...x, designAreas: areas } : x)));
    } catch { /* kaydedilemedi */ }
    setAreaMockup(null);
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex h-[90vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-2 border-b p-4">
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <Star className="h-4 w-4 text-amber-500" /> {title}
          </h3>

          {/* Karsilastirma: su anki secim */}
          {compare && (
            <div className="flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50/60 px-2 py-1">
              <img src={abs(compare.imageUrl)} alt="" className="h-9 w-9 rounded object-cover" />
              <span className="max-w-[160px] truncate text-[11px] text-amber-800">Seçili: {compare.label}</span>
            </div>
          )}

          <span className="text-xs text-gray-400">{shown.length} / {all.length}</span>

          <div className="relative ml-auto">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
              placeholder="Ara: renk, marka, model, ad…"
              className="w-60 rounded-lg border border-gray-200 py-1.5 pl-7 pr-3 text-sm focus:border-amber-400 focus:outline-none"
            />
          </div>

          <select value={folderId} onChange={(e) => setFolderId(e.target.value)}
            title="Klasöre göre süz (alt klasörler dahil)"
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs">
            <option value="">Tüm klasörler</option>
            {folderOptions.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>

          {productTypeKey && (
            <label className="flex items-center gap-1 text-xs text-gray-600">
              <input type="checkbox" checked={onlyType} onChange={(e) => setOnlyType(e.target.checked)} />
              {productTypeKey}
            </label>
          )}
          <label className="flex items-center gap-1 text-xs text-gray-600">
            <input type="checkbox" checked={onlyStarred} onChange={(e) => setOnlyStarred(e.target.checked)} />
            Yıldızlı
          </label>
          <label className="flex items-center gap-1 text-xs text-gray-600">
            <input type="checkbox" checked={onlyWithArea} onChange={(e) => setOnlyWithArea(e.target.checked)} />
            Alanı olanlar
          </label>

          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1">
        <div className="flex-1 overflow-y-auto p-4">
          {current && (
            <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
              <p className="mb-2 text-xs font-semibold text-gray-700">
                Bu listedeki mockuplar ({cur.length}) <span className="font-normal text-gray-500">— ilk sıra ana görsel; aşağıdan seçilen başa eklenir</span>
                {collection && <span className="font-normal text-gray-500"> · grup: {collection.name}</span>}
              </p>
              {cur.length === 0 ? <p className="text-xs text-gray-400">Henüz mockup yok.</p> : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-8">
                  {cur.map((id, i) => {
                    const m = all.find((x) => x.id === id);
                    const inColl = coll.includes(id);
                    const move = (to: number) => { const n = [...cur]; n.splice(to, 0, n.splice(i, 1)[0]); setCurrent(n); };
                    return (
                      <div key={id} className={`overflow-hidden rounded-lg border-2 bg-white ${i === 0 ? "border-amber-400" : "border-gray-200"}`}>
                        <div className="relative aspect-square bg-gray-100">
                          {m && <img src={abs(m.thumbnailUrl || m.imageUrl)} alt={m.label} className="h-full w-full object-cover" />}
                          <span className={`absolute left-1 top-1 rounded px-1 text-[9px] font-bold text-white ${i === 0 ? "bg-amber-400" : "bg-black/60"}`}>{i === 0 ? "★ Ana" : i + 1}</span>
                          <button onClick={() => setCurrent(cur.filter((x) => x !== id))} title="Listeden çıkar" aria-label="Listeden çıkar"
                            className="absolute right-1 top-1 rounded bg-red-500/90 p-0.5 text-white hover:bg-red-600"><X className="h-3 w-3" /></button>
                        </div>
                        <div className="space-y-1 p-1">
                          <p className="truncate text-[10px] text-gray-600" title={m?.label}>{m?.label ?? "…"}</p>
                          <div className="flex items-center justify-between gap-0.5">
                            <button disabled={i === 0} onClick={() => move(i - 1)} title="Sola taşı" aria-label="Sola taşı" className="rounded p-0.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"><ChevronLeft className="h-3 w-3" /></button>
                            {i > 0 && <button onClick={() => move(0)} title="Ana görsel yap" aria-label="Ana görsel yap" className="rounded p-0.5 text-amber-500 hover:bg-amber-50"><Star className="h-3 w-3" /></button>}
                            {m && <button onClick={() => setAreaMockup(m as unknown as CustomMockup)} title="Alanı düzenle" aria-label="Alanı düzenle" className="rounded p-0.5 text-gray-500 hover:bg-gray-100"><Pencil className="h-3 w-3" /></button>}
                            <button disabled={i === cur.length - 1} onClick={() => move(i + 1)} title="Sağa taşı" aria-label="Sağa taşı" className="rounded p-0.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"><ChevronRight className="h-3 w-3" /></button>
                          </div>
                          {collection && (
                            <button onClick={() => void toggleCollection(id)} disabled={collBusy === id}
                              className={`flex w-full items-center justify-center gap-1 rounded border py-0.5 text-[10px] disabled:opacity-50 ${
                                inColl ? "border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-600" : "border-amber-300 bg-amber-50 font-medium text-amber-700 hover:bg-amber-100"
                              }`}>
                              {inColl ? <><FolderMinus className="h-2.5 w-2.5" /> Gruptan çıkar</> : <><FolderPlus className="h-2.5 w-2.5" /> Gruba ekle</>}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : shown.length === 0 ? (
            <p className="py-20 text-center text-sm text-gray-400">Eşleşen mockup yok.</p>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {shown.map((m) => {
                const ok = mockupHasArea(m);
                const on = selected.has(m.id);
                return (
                  <div key={m.id}
                    className={`overflow-hidden rounded-lg border-2 ${
                      on ? "border-emerald-500 ring-2 ring-emerald-200" : "border-gray-200 hover:border-amber-400"
                    }`}>
                    <button type="button" onClick={() => (current ? setCurrent([m.id, ...cur]) : onPick(m))} className="relative block w-full">
                      <div className="aspect-square bg-gray-100">
                        <img src={abs(m.thumbnailUrl || m.imageUrl)} alt={m.label} loading="lazy"
                          className="h-full w-full object-cover" />
                      </div>
                      {m.isSelling && <Star className="absolute left-1 top-1 h-4 w-4 fill-amber-400 text-amber-400 drop-shadow" />}
                      {m.bigPrint && <span className="absolute right-1 top-1 rounded bg-orange-500 px-1 py-0.5 text-[9px] font-bold text-white shadow" title="Big print: seçilirse liste big print olur (+$5)">BIG</span>}
                      {on && <span className="absolute right-1 top-1 rounded bg-emerald-600 px-1 text-[9px] text-white">seçili</span>}
                      {collection && coll.includes(m.id) && <span className="absolute bottom-1 right-1 rounded bg-indigo-600/90 px-1 text-[9px] text-white" title={`"${collection.name}" grubunda`}>grupta</span>}
                      {!ok && (
                        <span className="absolute bottom-1 left-1 right-1 rounded bg-amber-500/90 px-1 py-0.5 text-center text-[9px] font-medium text-white">
                          baskı alanı yok
                        </span>
                      )}
                    </button>
                    <div className="p-1.5">
                      {/* Baslik TAM gorunur — kirpilmaz */}
                      <p className="text-[11px] leading-snug text-gray-700">{m.label}</p>
                      {(m.brand || m.model || m.color) && (
                        <p className="mt-0.5 text-[10px] leading-snug text-gray-400">
                          {[m.brand, m.model, m.color].filter(Boolean).join(" · ")}
                        </p>
                      )}
                      <button
                        onClick={() => setAreaMockup(m as unknown as CustomMockup)}
                        className={`mt-1 flex w-full items-center justify-center gap-1 rounded border py-0.5 text-[10px] ${
                          ok ? "border-gray-200 text-gray-500 hover:bg-gray-50" : "border-amber-300 bg-amber-50 text-amber-700"
                        }`}
                      >
                        <Pencil className="h-2.5 w-2.5" /> {ok ? "Alanı düzenle" : "Alan belirle"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sag: ilham listesinin fotografi BUYUK — karsilastirma icin */}
        {inspiration?.imageUrl && (
          <div className="hidden w-80 shrink-0 flex-col border-l bg-gray-50 p-3 lg:flex">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700">İlham listesi</p>
            <img src={abs(inspiration.imageUrl)} alt="" className="w-full rounded-lg border border-gray-200 object-contain" />
            {inspiration.title && (
              <p className="mt-2 text-[11px] leading-snug text-gray-600">{inspiration.title}</p>
            )}
            {compare && (
              <>
                <p className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Seçili satan mockup</p>
                <img src={abs(compare.imageUrl)} alt="" className="w-full rounded-lg border border-amber-300 object-contain" />
                <p className="mt-1 text-[11px] text-gray-600">{compare.label}</p>
              </>
            )}
          </div>
        )}
        </div>

        <div className="flex items-center justify-between border-t p-3 text-[11px] text-gray-500">
          <span>"Baskı alanı yok" işaretli mockupta tasarım yerleştirilemez — karttaki düğmeden alanı belirleyin.</span>
          <button onClick={onClose}
            className="rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-indigo-700">
            {mode === "multi" ? "Bitti" : "Kapat"}
          </button>
        </div>
      </div>

      {/* Alan duzenleyici — kendi tam ekran katmanini acar */}
      {areaMockup && (
        <div onClick={(e) => e.stopPropagation()}>
          <DesignAreaEditor
            mockup={areaMockup}
            onSave={(areas) => saveAreas(areaMockup.id, areas)}
            onCancel={() => setAreaMockup(null)}
            testDesignLight={testDesignLight}
            testDesignDark={testDesignDark}
            {...(apparel ? { apparel: { vars: supplierVars, onSaved: () => undefined } } : {})}
          />
        </div>
      )}
    </div>
  );
}
